import { Injectable, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { buildRouteBreadcrumbs } from '@enacom/ui';
import type { BreadcrumbItem } from '@enacom/ui';

/**
 * Serviço global de breadcrumb.
 *
 * Modo automático: reconstrói a trilha a partir dos metadados `data.breadcrumb` das rotas
 * a cada NavigationEnd. Overrides são limpos automaticamente na navegação.
 *
 * Modo manual: `set()` substitui a base de itens completamente até `clear()`.
 *
 * Em ambos os modos, `update()`, `updateLast()`, `insert()` e `push()` funcionam
 * como patches sobre os itens base.
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);

  /** Itens gerados automaticamente a partir da rota ativa. */
  private readonly _routeItems = signal<readonly BreadcrumbItem[]>([]);

  /** Substituto manual completo da base de itens (set/clear). */
  private readonly _manualItems = signal<readonly BreadcrumbItem[] | null>(null);

  /** Patches parciais indexados por posição final. Limpos a cada navegação. */
  private readonly _patches = signal<Map<number, Partial<BreadcrumbItem>>>(new Map());

  /** Itens inseridos dinamicamente (ex.: nome de experimento no meio da trilha). */
  private readonly _inserts = signal<{ index: number; item: BreadcrumbItem }[]>([]);

  /** Itens publicados — lidos pelo TopNavbar. */
  readonly items = computed<readonly BreadcrumbItem[]>(() => {
    const manual = this._manualItems();
    let base: BreadcrumbItem[] = manual !== null ? [...manual] : [...this._routeItems()];

    // Inserts: aplicados sobre os itens base, ordenados por índice crescente
    const inserts = [...this._inserts()].sort((a, b) => a.index - b.index);
    let offset = 0;
    for (const { index, item } of inserts) {
      base.splice(index + offset, 0, item);
      offset++;
    }

    // Garantir que o último item nunca tenha url (página atual, não clicável)
    if (base.length > 0) {
      base[base.length - 1] = { ...base[base.length - 1], url: undefined, queryParams: undefined };
    }

    // Patches por índice — aplicados sobre o array pós-inserts
    const patches = this._patches();
    if (patches.size > 0) {
      base = base.map((item, i) => {
        const patch = patches.get(i);
        return patch ? { ...item, ...patch } : item;
      });
    }

    return base;
  });

  constructor() {
    // Inicializar com a rota atual (para quando o serviço é criado após a navegação)
    toSignal(
      this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
      { initialValue: null },
    );

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this._onNavigationEnd());
  }

  private _onNavigationEnd(): void {
    this._clearOverrides();
    const root = this.router.routerState.snapshot.root;
    this._routeItems.set(buildRouteBreadcrumbs(root));
  }

  private _clearOverrides(): void {
    this._manualItems.set(null);
    this._patches.set(new Map());
    this._inserts.set([]);
  }

  // ─── API pública ───────────────────────────────────────────────────────────

  /** Substitui toda a base de itens de forma manual. Persiste até próxima navegação. */
  set(items: readonly BreadcrumbItem[]): void {
    this._manualItems.set(items);
  }

  /** Volta ao modo automático e limpa todos os overrides. */
  clear(): void {
    this._clearOverrides();
  }

  /**
   * Patch parcial em um item pelo índice no array final (após inserts).
   * Não afeta outros itens.
   */
  update(index: number, partial: Partial<BreadcrumbItem>): void {
    this._patches.update(map => {
      const next = new Map(map);
      next.set(index, { ...(next.get(index) ?? {}), ...partial });
      return next;
    });
  }

  /**
   * Atalho para atualizar o label do último item — caso mais comum (nome dinâmico).
   * Opcionalmente aceita um patch parcial adicional.
   */
  updateLast(label: string, extra?: Partial<Omit<BreadcrumbItem, 'label'>>): void {
    const len = this.items().length;
    if (len === 0) return;
    this.update(len - 1, { label, ...extra });
  }

  /**
   * Substitui todos os inserts dinâmicos de uma vez (idempotente).
   * Útil para re-sincronizar após NavigationEnd limpar overrides.
   */
  setInserts(inserts: readonly { index: number; item: BreadcrumbItem }[]): void {
    this._inserts.set([...inserts]);
  }

  /**
   * Insere um item em determinada posição da trilha base.
   * Útil para adicionar o nome do experimento entre "Experimentos" e "Comparador".
   */
  insert(index: number, item: BreadcrumbItem): void {
    this._inserts.update(list => [...list, { index, item }]);
  }

  /**
   * Append de item ao final da trilha base (antes de aplicar garantia de último sem url).
   */
  push(item: BreadcrumbItem): void {
    const manual = this._manualItems();
    const baseLen = manual !== null
      ? manual.length
      : this._routeItems().length;
    const insertOffset = this._inserts().length;
    this.insert(baseLen + insertOffset, item);
  }
}
